/**
 * Reconciling what the customer ASKED FOR with how the matched item is actually packed.
 *
 * The customer writes "3 kg washing powder". The catalogue item is
 * `LAUNDRY SOAP POWDER TIDE 1.5KG`. Nobody sells 3 KG of it — you order **2**. Until now the
 * purchaser did that division in their head, on every line, and the quantity they typed was
 * whatever they worked out, with nothing on screen to check it against.
 *
 * ---- Where the pack size comes from, and why that is not a shortcut ----
 *
 * From the ITEM NAME. Measured 2026-08-11 over 234 distinct live items, **91 (39%) state their
 * pack size in the name** — `TIDE 1.5KG`, `TIDE DETERGENT POWDER 270 GM`, `GRAPHITE POWDER 2.0 KG`,
 * `NOODLES EGG 400 GM PKT`. There is no structured field for it: `Item` on this bench is a lean
 * doctype with no pack size, no weight, no volume, and `uom_conversions` describes only how one
 * ORDERING unit relates to another (CASE = 6), never what is inside one.
 *
 * So parsing the name is not a workaround for a field we forgot to read — it is the only place
 * the fact exists. When the backend grows a real pack-size field, `parsePackSize` is the one
 * function that changes.
 *
 * ---- Why arithmetic and not a language model ----
 *
 * This recommendation CHANGES AN ORDER QUANTITY. `3 ÷ 1.5 = 2` can be checked by the purchaser
 * at a glance and pinned by a test; "the model suggested 2" can be neither. The 61% of names
 * that do not parse get no recommendation at all, which is the honest outcome — a wrong pack
 * size silently doubles an order, and a name like `FRESH EGG WHITE 30 S x 12 TRAY` is exactly
 * where a confident guess would do it. Every recommendation this module makes carries the
 * arithmetic that produced it, for display next to the button that applies it.
 */

/** Units that can be compared with each other. Nothing converts across families. */
export type UnitFamily = 'mass' | 'volume' | 'count'

/**
 * Token → family and how many canonical units it is worth (grams, millilitres, or items).
 *
 * Deliberately narrow. `CM`, `MM`, `M`, `INCH` are absent because a length in a name
 * (`SAFETY SHOES … 29 CM`) is a size, not a pack quantity, and treating it as one would invent a
 * recommendation for an item that has no pack size at all.
 */
const UNITS: Record<string, { family: UnitFamily; canonical: number }> = {
  kg: { family: 'mass', canonical: 1000 },
  kgs: { family: 'mass', canonical: 1000 },
  g: { family: 'mass', canonical: 1 },
  gm: { family: 'mass', canonical: 1 },
  gms: { family: 'mass', canonical: 1 },
  grm: { family: 'mass', canonical: 1 },
  grms: { family: 'mass', canonical: 1 },
  gram: { family: 'mass', canonical: 1 },
  grams: { family: 'mass', canonical: 1 },
  mg: { family: 'mass', canonical: 0.001 },
  l: { family: 'volume', canonical: 1000 },
  ltr: { family: 'volume', canonical: 1000 },
  ltrs: { family: 'volume', canonical: 1000 },
  litre: { family: 'volume', canonical: 1000 },
  litres: { family: 'volume', canonical: 1000 },
  liter: { family: 'volume', canonical: 1000 },
  ml: { family: 'volume', canonical: 1 },
  cl: { family: 'volume', canonical: 10 },
  pcs: { family: 'count', canonical: 1 },
  pc: { family: 'count', canonical: 1 },
  nos: { family: 'count', canonical: 1 },
  no: { family: 'count', canonical: 1 },
  ea: { family: 'count', canonical: 1 },
  each: { family: 'count', canonical: 1 },
  unit: { family: 'count', canonical: 1 },
  units: { family: 'count', canonical: 1 },
  // `30S` / `30 S` is how this catalogue writes "a tray of 30" (`EGG CHICKEN WHITE LARGE 30S`).
  // Only ever reached attached to a number, so it cannot swallow a stray letter elsewhere.
  s: { family: 'count', canonical: 1 },
}

/** A quantity with a unit, plus that quantity expressed in its family's canonical unit. */
export interface Measure {
  qty: number
  /** As written, upper-cased for display — `KG`, `GM`, `PCS`. */
  unit: string
  family: UnitFamily
  /** `qty` in grams / millilitres / items, so two measures in a family can be divided. */
  canonical: number
}

const NUMBER = String.raw`\d+(?:[.,]\d+)?`
/** `12 * 90 G` / `12x90G` — a multipack states its own count and its per-piece size. */
const MULTIPACK = new RegExp(String.raw`(${NUMBER})\s*[x*×]\s*(${NUMBER})\s*([a-z]+)\b`, 'i')
const SIMPLE = new RegExp(String.raw`(${NUMBER})\s*([a-z]+)\b`, 'gi')

const toNumber = (raw: string): number => Number(raw.replace(',', '.'))

function measure(qty: number, token: string): Measure | null {
  const unit = UNITS[token.toLowerCase()]
  if (!unit || !Number.isFinite(qty) || qty <= 0) return null
  return { qty, unit: token.toUpperCase(), family: unit.family, canonical: qty * unit.canonical }
}

/**
 * The first quantity-with-unit in a piece of free text — what the CUSTOMER asked for.
 *
 * First, not best: a customer line reads "3 kg washing powder tide", and the leading number is
 * the one they meant. A later number in the same line is part of the description.
 */
export function parseMeasure(text: string): Measure | null {
  for (const match of text.matchAll(SIMPLE)) {
    const found = measure(toNumber(match[1]), match[2])
    if (found) return found
  }
  return null
}

/**
 * What ONE of this item contains, read from its name.
 *
 * A multipack wins over a bare number when both are present: `DAIRY MILK FRUIT & NUT 12*90 G` is
 * 1,080 g in the box, and reading it as "90 g" would recommend twelve times too many.
 */
export function parsePackSize(itemName: string): Measure | null {
  const multi = MULTIPACK.exec(itemName)
  if (multi) {
    const each = measure(toNumber(multi[2]), multi[3])
    if (each) {
      const count = toNumber(multi[1])
      if (Number.isFinite(count) && count > 0) {
        return { ...each, qty: each.qty * count, canonical: each.canonical * count }
      }
    }
  }
  return parseMeasure(itemName)
}

export interface QtyRecommendation {
  /** What the customer asked for, as written. */
  request: Measure
  /** What one unit of the matched item contains. */
  pack: Measure
  /** How many whole units to order — never fractional, never zero. */
  qty: number
  /** True when the packs divide the request exactly, with nothing over. */
  exact: boolean
  /** How much the order overshoots by, in the request's own unit. `0` when exact. */
  overBy: number
  /** The arithmetic, for display beside the button that applies it. */
  explanation: string
}

/** Two decimals, so a recurring division does not print as `2.6666666666666665`. */
const round2 = (n: number): number => Math.round(n * 100) / 100

/**
 * How many of this item cover what the customer asked for.
 *
 * `null` — meaning "say nothing" — whenever the two cannot be compared: the name states no pack
 * size (61% of the catalogue), the customer's text states no quantity, or the two are in
 * different families. Comparing 3 KG against a 30-piece tray is not a hard problem to solve
 * badly; it is one where any answer would be invented.
 *
 * Rounds UP. Ordering 2 packs against a 4 KG request when each holds 1.5 KG leaves the customer
 * short, and a shortfall is the one error a procurement screen must not round towards.
 */
/**
 * Units that COUNT things rather than measure them.
 *
 * A line priced in one of these is ordering N objects, so any weight or volume in the customer's
 * text describes the object, not the order — see `recommendQty`.
 */
const COUNT_UNITS = new Set([
  'pkt', 'pkts', 'packet', 'packets', 'pack', 'packs',
  'nos', 'no', 'pc', 'pcs', 'piece', 'pieces', 'ea', 'each', 'unit', 'units',
  'box', 'boxes', 'ctn', 'carton', 'cartons', 'case', 'cases',
  'doz', 'dozen', 'pr', 'pair', 'pairs', 'bag', 'bags', 'roll', 'rolls',
  'tin', 'tins', 'can', 'cans', 'btl', 'bottle', 'bottles', 'jar', 'jars',
  'tube', 'tubes', 'set', 'sets', 'coil', 'coils', 'drum', 'drums', 'pail', 'pails',
])

/**
 * How many packs to order, when that is a question the text can actually answer.
 *
 * `lineUom` is the unit the LINE is measured in, and it decides whether there is a question at
 * all. Reported live 2026-08-31:
 *
 *     "NOODLES-VEG ( 50 Gm .PKT)"  ·  100 PKT  ·  item "NOODLES LOVEME VEGITABLE 65 GM PKT"
 *       → "50 GM ÷ 65 GM each = 0.77 → order 1"
 *
 * The customer wants a hundred packets of a 50-gram noodle. The "50 Gm" is the PACK SIZE they
 * asked for, not the total they want — so it says something about substituting our 65 GM packet
 * for their 50 GM one, and nothing about how many to order. The screen offered "set qty to 1",
 * which replaced 100 packets with 1.
 *
 * So: when the line is COUNTED (PKT, Nos, BOX…), any weight in the text describes the object and
 * no quantity is derivable. When the line is MEASURED in the same family as the text ("3 KG" on a
 * line in KG), the text really is a total and the arithmetic is the whole point.
 *
 * An unknown or absent `lineUom` behaves as before — better a useful hint than a silent drop.
 */
export function recommendQty(
  requestText: string,
  itemName: string,
  lineUom?: string,
): QtyRecommendation | null {
  // The line counts objects, so the text's measure is a description of one. See above.
  if (lineUom && COUNT_UNITS.has(lineUom.trim().toLowerCase())) return null

  const request = parseMeasure(requestText)
  const pack = parsePackSize(itemName)
  if (!request || !pack || request.family !== pack.family) return null

  const exactPacks = request.canonical / pack.canonical
  // A pack that IS the request adds nothing: "3 KG ÷ 3 KG each = 1" is a sentence nobody needs.
  if (exactPacks === 1) return null

  const qty = Math.max(1, Math.ceil(round2(exactPacks)))
  const exact = round2(exactPacks) === qty
  const deliveredInRequestUnit = (qty * pack.canonical) / (request.canonical / request.qty)
  const overBy = exact ? 0 : round2(deliveredInRequestUnit - request.qty)

  const head = `${request.qty} ${request.unit} ÷ ${pack.qty} ${pack.unit} each = ${round2(exactPacks)}`
  return {
    request,
    pack,
    qty,
    exact,
    overBy,
    explanation: exact ? head : `${head} → order ${qty} (${round2(deliveredInRequestUnit)} ${request.unit}, ${overBy} over)`,
  }
}

/**
 * The unit a line should carry after its item changes — `null` when the one it has is fine.
 *
 * ---- Why this exists: matching an item can INVALIDATE the unit ----
 *
 * A line extracted as `3 KG` and then matched to `RAYTHEON FU AMPLIFIER` (which accepts only
 * `Nos`) kept its `KG` and sat there reading *"KG not in master · available: Nos (base)"*. That is
 * not a cosmetic warning: `uom` is a Frappe Link, so the line is in a state the server refuses to
 * save, and the purchaser has to notice and fix a field they never touched.
 *
 * The unit is a property of the ITEM, so changing the item has to reconsider it. Four steps, in
 * order of how much they respect what a human already said:
 *
 *   1. the unit the CUSTOMER asked in, if the new item accepts it — their words win. The
 *      recorded `customer_uom` first (the create wrote it for exactly this), the wording only
 *      when nothing was recorded: "BREAD SLICE WHITE 600GM" reads as no unit at all, and the
 *      line sat on KG asking "confirm which" while PKT was on record (Akhil, 2026-09-17)
 *   2. the unit already on the line, if the new item accepts it — nothing to fix
 *   3. an equivalent spelling of the line's unit (`KGS` on a line, `KG` in the master)
 *
 * There is NO fourth step any more (Akhil, 2026-09-16). It used to fall back to the item's base
 * unit, which silently changed what a line meant (`3 KG` becoming `3 Nos`) and looked decided.
 * Now the line keeps the unit it has — a valid Link, since it was read from the server — and
 * `uomChoice.ts` marks the line undecided: the row shows the picker empty with the item's units
 * to pick from, and nothing goes to a supplier or a customer until one is picked.
 */
export function uomAfterItemChange(
  currentUom: string,
  customerText: string,
  accepted: readonly string[],
  baseUom: string,
  customerUom = '',
): string | null {
  if (accepted.length === 0) return null

  const recorded = customerUom.trim() ? matchItemUom(customerUom.trim(), accepted) : null
  const said = recorded ? null : parseMeasure(customerText)
  const fromCustomer = recorded ?? (said ? matchItemUom(said.unit, accepted) : null)
  if (fromCustomer) return fromCustomer === currentUom ? null : fromCustomer

  if (accepted.some((u) => u === currentUom)) return null

  const equivalent = matchItemUom(currentUom, accepted)
  if (equivalent) return equivalent === currentUom ? null : equivalent

  void baseUom
  return null
}

/**
 * The item's own spelling of a unit the customer named — `null` when it accepts no such unit.
 *
 * Case- and plural-insensitive, because a customer writes `kg` and the master says `KG`, and
 * because the value handed back must be the MASTER's spelling: it is written to a Frappe Link,
 * where `kg` and `KG` are different documents and the wrong one fails the whole save.
 */
export function matchItemUom(customerUnit: string, acceptedUoms: readonly string[]): string | null {
  const wanted = customerUnit.trim().toLowerCase()
  if (wanted === '') return null
  const direct = acceptedUoms.find((u) => u.trim().toLowerCase() === wanted)
  if (direct) return direct
  // `KGS` from the customer against `KG` in the master, and the reverse.
  const family = UNITS[wanted]
  if (!family) return null
  return (
    acceptedUoms.find((u) => {
      const known = UNITS[u.trim().toLowerCase()]
      return known && known.family === family.family && known.canonical === family.canonical
    }) ?? null
  )
}
